import {BoxModel, BoxWedlet} from "back/edit/wed/wedlets/box/box";
import {WED, WedletModelBase} from "back/edit/wed/wedCore";
import {IInfo, IInfoConsumer, IInfoProducer, InfoBrokerBasic} from "lib/commons/infos";
import {IChildWedlet, IElementWedlet, IForksWedlet, IParentWedlet, IS_EltWedlet, isParentWedlet, IWedlet, OVisitWedletOptions} from "back/edit/wed/wedlets/wedlet";
import {DOM, JSX} from "lib/commons/xml/dom";
import {IXAddr, XA} from "lib/commons/xml/xAddr";
import {IJmlNode, IJmlSet, IJmlSubSet} from "lib/commons/xml/jml";
import {OffscreenEltLeafWedlet, OffscreenEltWedlet, OffscreenModel, OffscreenStrWedlet} from "back/edit/wed/wedlets/offscreen/offscreen";
import {IDocBatch} from "lib/edit/docHolder";
import {IWedEditorBoxSel} from "back/edit/wed/features/boxSel";
import {REMOTE} from "lib/core/remote";
import SCCORE_NS = DOM.SCCORE_NS;

/**
 * Modèle de weldet de la famille Box constituant le point racine pour intégrer un éditeur SpTe.
 * Ce point doit être un bind de type element. Cet élement doit avoir dans sa descendance
 * à la fois la ref à la source SpTe à enrichir (image, audio, video...),
 * et l'arborescence des segments (Sp et/ou Te).
 *
 * La configuration de ce wedlet est similaire à un wedlet "Box" standard en intégrant des widgets dédiés :
 *  - <spte-wallpaper wedlet-modes=""/> <spte-video wedlet-modes=""/> ou <spte-audiowaves wedlet-modes=""/> :
 *    [card 1] réprésentation de la source. Le fork fournit le wedlet de type "spteSource", et pour
 *    wallpaper et video, le fork fournit aussi les wedlets de type "spte2D" dessinant les zones. Dans le cas
 *    d'une video, seules les area actifs à la position courante du curseur temporel sont affichés.
 *  - <spte-timeline wedlet-mode=""/> : [card *] ligne de temps (fork de wedlet de type "spteTime")
 *  - <spte-outline wedlet-mode=""/> : [card *] plan des segments sp et/ou te (fork de wedlet de type "outline")
 *  - <spte-focusfilter>...</spte-focusfilter> : [card ?] container des parts de type segment pour gérer la restriction
 *    d'affichage au segments qui sont sélectionnés.
 *
 * Ce wedlet, par un infoBroker dédié qui dispatch les info entre tous ces composants, coordonne :
 * - les infos de la source (durée totale et/ou dimensions),
 * - la sélection courante des segments,
 * - le curseur temporel courant.
 */
class BoxSpTeRootModel extends BoxModel {
	initModel(cnf: Element) {
		super.initModel(cnf);
		this.wedletClass = BoxSpTeRootWedlet;
	}
}

WED.registerWedletModel("BoxSpTeRoot", BoxSpTeRootModel);


/**
 * Modèle de wedlet fournissant la source SpTe.
 * 1ère impl générique triviale ou la source est une url en attr ou text node du flux xml.
 */
class SpTeSourceModel extends WedletModelBase {
	createWedlet(parent: IParentWedlet): IChildWedlet {
		return new SpTeSourceUrlWedlet(this, parent);
	}
}

WED.registerWedletModel("SpTeSourceUrl", SpTeSourceModel);

/**
 * Modèle de wedlet pour bind de <sc:spacial/>
 */
class SpTeSegmentModel extends OffscreenModel {
	createWedlet(parent: IParentWedlet): IChildWedlet {
		return new SpTeSegmentWedlet(this, parent);
	}
}

WED.registerWedletModel("SpTeSegment", SpTeSegmentModel);

/**
 * Modèle de wedlet pour bind de <sc:spacial/>
 */
export class SpTeSpatialModel extends WedletModelBase {
	createWedlet(parent: IParentWedlet): IChildWedlet {
		return new SpTeSpatialWedlet(this, parent);
	}
}

WED.registerWedletModel("SpTeSpatial", SpTeSpatialModel);

/**
 * Modèle de wedlet pour bind de <sc:temporal/>
 */
export class SpTeTemporalModel extends WedletModelBase {
	level: number;

	createWedlet(parent: IParentWedlet): IChildWedlet {
		return new SpTeTemporalWedlet(this, parent);
	}
}

WED.registerWedletModel("SpTeTemporal", SpTeTemporalModel);

//############ WEDLETS #############


/**
 * Wedlet racine pour le spTe editor.
 */
export class BoxSpTeRootWedlet extends BoxWedlet implements IForksWedlet, IInfoConsumer {

	xaPart: number;

	spteInfoBroker = new InfoBrokerBasic();
	spteSource: IEltSpTeSource;
	spteViews: IEltSpTeView[];

	visitWedletForks(visitor: (this: void, wedlet: IWedlet) => ("stop" | void), options?: OVisitWedletOptions): "stop" | void {
		if (this.spteSource && visitor(this.spteSource.forkedWedlet) === 'stop') return 'stop';
		if (this.spteViews) for (const v of this.spteViews) if (visitor(v.forkedWedlet) === 'stop') return 'stop';
	}

	setSpTeSource(spteSource: IEltSpTeSource) {
		this.spteSource = spteSource;
	}

	addSpTeView(spteView: IEltSpTeView) {
		if (!this.spteViews) this.spteViews = [];
		this.spteViews.push(spteView);
	}

	protected createElement(root: Element) {
		super.createElement(root);
		this.element.addEventListener("focusin", this.onFocusin);
		this.spteInfoBroker.addConsumer(this);
	}

	/** Capte le focus dans les segments descendants. */
	protected onFocusin(this: IElementWedlet & HTMLElement, ev: FocusEvent) {
		if (ev.target !== this) {
			//Le focus n'est pas nous même (ie des widgets spte)
			const eltWedlet = DOM.findParentOrSelf(ev.target as HTMLElement, this, IS_EltWedlet);
			if (eltWedlet) (this.wedlet as BoxSpTeRootWedlet).spteInfoBroker.dispatchInfo(new InfoSpTeFocus(eltWedlet.wedlet.wedAnchor), this.wedlet);
		}
	}

	onInfo(info: IInfo): void {
		if (info instanceof InfoSpTeFocus) {
			//Un widget spTeEditor représentant un segment est focusé
			// => on marque le segment comme sélectionné du coté des wedlets box de l'éditeur principal.
			const boxSelMgr = (this.wedMgr.wedEditor as IWedEditorBoxSel).boxSelMgr;
			boxSelMgr.markWedlet(boxSelMgr.findAroundXAddr(info.focusXa), info.ensureVisible);
		}
	}
}

/** Impl triviale du wedlet fournissant la source lorsque la source est directement une url dans la source XML éditée. */
export class SpTeSourceUrlWedlet extends OffscreenStrWedlet {
	protected setText(txt: string) {
		super.setText(txt);
		this.initUrl(txt);
	}

	protected initUrl(txt: string) {
		SPTE.findSpTeForkedWedlet(this).spTeViews[0].wedlet.spteSource.setUrl(REMOTE.urlRemoteContent(txt, this.wedMgr.reg) || txt);
	}
}

export interface ISpTeSpatialWedlet extends IChildWedlet {
	getShape(): string | null

	getCoords(): string | null

	updateSpatial(batch: IDocBatch, coords: string, shape?: string): IDocBatch
}


export interface ISpTeTemporalWedlet extends IChildWedlet {
	getStart(): string | null

	getEnd(): string | null

	getLevel(): number

	getHandleable(): 'always' | 'never' | 'restrictToContainer'

	updateTemporal(batch: IDocBatch, start: string, end: string): IDocBatch
}

/** Wedlet pour binder un segment. */
export class SpTeSegmentWedlet extends OffscreenEltWedlet {

	//FIXME creation / suppression : for (let v of SPTE.findSpTeForkedWedlet(this).spTeViews) v.redrawSpTeView();

	getSpatial(): ISpTeSpatialWedlet {
		let sp: ISpTeSpatialWedlet = null;
		const visitor = (w: IChildWedlet): void | 'stop' => {
			if (w instanceof SpTeSpatialWedlet) {
				sp = w;
				return 'stop';
			} else if (isParentWedlet(w)) {
				if ((w as IParentWedlet).visitWedletChildren(0, Number.MAX_SAFE_INTEGER, visitor) === 'stop') return 'stop';
			}
		};
		this.visitWedletChildren(0, Number.MAX_SAFE_INTEGER, visitor);
		return sp;
	}

	getTemporal(): ISpTeTemporalWedlet {
		let te: ISpTeTemporalWedlet = null;
		const visitor = (w: IChildWedlet): void | 'stop' => {
			if (w instanceof SpTeTemporalWedlet) {
				te = w;
				return 'stop';
			} else if (isParentWedlet(w)) {
				if ((w as IParentWedlet).visitWedletChildren(0, Number.MAX_SAFE_INTEGER, visitor) === 'stop') return 'stop';
			}
		};
		this.visitWedletChildren(0, Number.MAX_SAFE_INTEGER, visitor);
		return te;
	}

	/** todo */
	getTitle(): string {
		return null
	}

	bindWithNode(xaOffset: number, node: IJmlNode, children: IJmlSubSet): void | Promise<void> {
		const res = super.bindWithNode(xaOffset, node, children);
		if (res) return res.then(() => {this._redrawSpTeViews()});
		this._redrawSpTeViews();
	}

	onDelete(): void {
		super.onDelete();
		this._redrawSpTeViews();
	}

	protected _redrawSpTeViews() {
		for (const v of SPTE.findSpTeForkedWedlet(this).spTeViews) v.redrawSpTeView();
	}

	getParentSegmentWedlet(): SpTeSegmentWedlet {
		let parentW = this.wedParent;
		while (parentW) {
			if (parentW instanceof SpTeSegmentWedlet) return parentW;
			parentW = parentW.wedParent;
		}
		return null;
	}
}

/** Wedlet pour binder <sc:spatial/>. */
export class SpTeSpatialWedlet extends OffscreenEltLeafWedlet implements ISpTeSpatialWedlet {

	/** Customisation du nom du tag 'sc:shape'. ATT: impl actuelle limité au chgt de préfix, cf querySelector('shape') */
	get shapeTag(): string {return this.model.config.getAttribute('shapeTag') || 'sc:shape'}

	/** Customisation du nom du tag 'sc:coords'. ATT: impl actuelle limité au chgt de préfix, cf querySelector('coords') */
	get coordsTag(): string {return this.model.config.getAttribute('coordsTag') || 'sc:coords'}

	getShape(): 'rect' | 'circle' | 'poly' | 'ellipse' | null {
		if (!this.content) return null;
		const elt = this.content.querySelector('shape');
		if (!elt) return null;
		return elt.textContent as any;
	}

	getCoords(): string | null {
		if (!this.content) return null;
		const elt = this.content.querySelector('coords');
		if (!elt) return null;
		return elt.textContent;
	}

	updateSpatial(batch: IDocBatch, coords: string, shape?: string) {
		if (!this.content) return; //la balise sc:spatial doit être créée en amont (autoCorrect schema...)
		const xaRoot = XA.freeze(this.wedAnchor);
		let rootAppend: IJmlSet;
		if (shape) {
			const shapeElt = this.content.querySelector('shape');
			if (shapeElt) {
				const xa = XA.append(xaRoot, DOM.computeOffset(shapeElt), 0);
				if (shapeElt.hasChildNodes()) batch.deleteSequence(xa, shapeElt.childNodes.length);
				batch.insertJml(xa, [shape]);
			} else {
				rootAppend = [{'': this.shapeTag}, [shape]];
			}
		}
		const coordsElt = this.content.querySelector('coords');
		if (coordsElt) {
			const xa = XA.append(xaRoot, DOM.computeOffset(coordsElt), 0);
			if (coordsElt.hasChildNodes()) batch.deleteSequence(xa, coordsElt.childNodes.length);
			batch.insertJml(xa, [coords]);
		} else {
			if (!rootAppend) rootAppend = [];
			rootAppend.push({'': this.coordsTag}, [coords]);
		}
		if (rootAppend) batch.insertJml(xaRoot, rootAppend);
		return batch;
	}

	protected setContent(content: Element) {
		this.content = content;
		for (const v of SPTE.findSpTeForkedWedlet(this).spTeViews) v.redrawSpTeView();
	}
}

/** Wedlet pour binder <sc:temporal/>. */
export class SpTeTemporalWedlet extends OffscreenEltLeafWedlet implements ISpTeTemporalWedlet {
	getStart(): string | null {
		if (!this.content) return null;
		const startElt = this.content.getElementsByTagNameNS(SCCORE_NS, 'start').item(0);
		return startElt.textContent;
	}

	getEnd(): string | null {
		if (!this.content) return null;
		const endElt = this.content.getElementsByTagNameNS(SCCORE_NS, 'end').item(0);
		return endElt.textContent;
	}

	getLevel(): number {
		const level = this.model.config.getAttribute('level');
		return level != null ? parseInt(level) : 0;
	}

	getHandleable(): "always" | "never" | "restrictToContainer" {
		const handleable = this.model.config.getAttribute('handleable');
		return handleable ? handleable as "always" | "never" | "restrictToContainer" : "always";
	}

	updateTemporal(batch: IDocBatch, start: string, end: string): IDocBatch {
		if (!this.content) return; //la balise sc:temporal doit être créée en amont (autoCorrect schema...)
		const xaRoot = XA.freeze(this.wedAnchor);
		let rootAppend: IJmlSet;

		const startElt = this.content.getElementsByTagNameNS(SCCORE_NS, 'start').item(0);
		if (startElt) {
			const xa = XA.append(xaRoot, DOM.computeOffset(startElt), 0);
			if (startElt.hasChildNodes()) batch.deleteSequence(xa, startElt.childNodes.length);
			batch.insertJml(xa, [start]);
		}
		else rootAppend = [{'': 'sc:start'}, [start]];

		const endElt = this.content.getElementsByTagNameNS(SCCORE_NS, 'end').item(0);
		if (endElt) {
			const xa = XA.append(xaRoot, DOM.computeOffset(endElt), 0);
			if (endElt.hasChildNodes()) batch.deleteSequence(xa, endElt.childNodes.length);
			batch.insertJml(xa, [end]);
		}
		else {
			if (!rootAppend) rootAppend = [];
			rootAppend.push([{'': 'sc:end'}, [end]]);
		}

		if (rootAppend) batch.insertJml(xaRoot, rootAppend);
		return batch;
	}

	protected setContent(content: Element) {
		this.content = content;
		for (const v of SPTE.findSpTeForkedWedlet(this).spTeViews) v.redrawSpTeView();
	}
}


/** Wedlet racine d'un fork : référence la ou les views à rafraichir. */
export interface ISpTeForkedWedlet extends IChildWedlet, IParentWedlet {
	spTeViews: IEltSpTeView[]
}


//############ CUSTOM ELEMENTS #############


/** View spte (wallpaper, video, timeline, outline...)*/
export interface IEltSpTeView extends IElementWedlet {
	wedlet: BoxSpTeRootWedlet

	/** Modèle pour cette view. */
	forkedWedlet: ISpTeForkedWedlet

	/** Appelé par les wedlets du forkedWedlet suite à une modification. */
	redrawSpTeView(): void
}

/**
 * View spte référençant la source SpTe
 * ex : spte-wallpaper, spte-video, spte-audiowaves...
 */
export interface IEltSpTeSource extends IEltSpTeView {
	/** */
	isSpTeSrcAvailable: boolean

	/** Source temporelle */
	spteSrcDuration: number | undefined

	/** Source spaciale */
	spteSrcSize: { w: number, h: number } | undefined

	/** Init la source par une url. */
	setUrl(url: string, noCleanup?: boolean): void

	/** Init de la waveform par une url. */
	setWaveformUrl?(url: string): void

	/** Définition de la durée quand celle-ci peut être déterminé indépendamment du chargement de la source */
	setDurationProp?(duration: number): void
}

//function IS_EltSpTeSource(elt: any): elt is IEltSpTeSource {return elt && ('isSpTeSrcAvailable' in elt)}


/**
 * Info d'un changement de focus. Le Xa est le wedlet termnial du focus,
 * qui n'est pas forcément le xa du segment, voire qui pourrait être un xa qui
 * est en dehors de tout segment (part non spTe).
 */
export class InfoSpTeFocus implements IInfo {
	constructor(public focusXa: IXAddr, public ensureVisible: boolean = false) {}

	infoHolders?: IInfoProducer[];
}

export namespace SPTE {

	/** Recherche le wedlet racine du fork pour retrouver les views SpTe à rafraichir. */
	export function findSpTeForkedWedlet(from: IWedlet): ISpTeForkedWedlet {
		while (from) {
			if ('spTeViews' in from) return from as ISpTeForkedWedlet;
			from = from.wedParent;
		}
		return null;
	}

	export function findSpTeSegmentWedlet(from: IWedlet): SpTeSegmentWedlet {
		while (from) {
			if (from instanceof SpTeSegmentWedlet) return from;
			from = from.wedParent;
		}
		return null;
	}
}
